<template>
  <div class="admin-section">
    <div class="section-header">
      <h2 class="section-title">ClinicalTrials.gov Study Search</h2>
      <form @submit.prevent="searchClinicalTrials" class="search-form">
        <Input v-model="clinicalTrialsQuery" placeholder="Enter keywords or condition" class="search-input" />
        <Button type="submit" variant="default" size="sm" class="refresh-btn">Search</Button>
      </form>
    </div>
    
    <div v-if="clinicalTrialsError" class="message error">{{ clinicalTrialsError }}</div>
    
    <!-- Results counter -->
    <div v-if="clinicalTrialsResults.length > 0" class="results-counter">
      <span v-if="nextPageToken">Loaded {{ clinicalTrialsResults.length }} results (more available)</span>
      <span v-else>{{ clinicalTrialsResults.length }} total results</span>
    </div>
    
    <div v-if="isLoadingClinicalTrials" class="loading-state">Searching...</div>
    <div v-else-if="clinicalTrialsResults.length === 0 && clinicalTrialsSearched" class="empty-state">No studies found.</div>
    <div v-else class="studies-list">
      <div v-for="study in clinicalTrialsResults" :key="study.nct_id" class="study-item">
        <div class="study-header">
          <div class="study-info" @click="toggleStudy(study.nct_id)" style="cursor: pointer;">
            <div class="study-name">{{ study.name }}</div>
            <div class="study-meta">
              <span class="study-sponsor">Sponsor: {{ study.sponsor || 'N/A' }}</span>
              <span class="study-source">Source: {{ study.source }}</span>
              <span v-if="study.nct_id" class="study-nctid">
                <a :href="`https://clinicaltrials.gov/ct2/show/${study.nct_id}`" target="_blank" @click.stop>NCT: {{ study.nct_id }}</a>
              </span>
            </div>
          </div>
          <div class="study-actions">
            <Button 
              @click.stop="importClinicalTrial(study)" 
              :disabled="isImportingClinicalTrial[study.nct_id] || isStudyImported(study.nct_id)" 
              variant="default" 
              size="sm" 
              :class="['refresh-btn', { 'imported-btn': isStudyImported(study.nct_id) }]"
            >
              <span v-if="isStudyImported(study.nct_id)">Already Imported</span>
              <span v-else-if="!isImportingClinicalTrial[study.nct_id]">Import</span>
              <span v-else>Importing...</span>
            </Button>
            <span class="expand-icon" :class="{ expanded: expandedStudies[study.nct_id] }" @click.stop="toggleStudy(study.nct_id)">
              ▼
            </span>
          </div>
        </div>
        <div v-if="expandedStudies[study.nct_id]" class="study-details">
          <div class="detail-section study-metadata">
            <strong>Study Metadata:</strong>
            <div class="metadata-grid">
              <div v-if="study.sponsor">
                <span class="metadata-label">Sponsor:</span>
                <span class="metadata-value">{{ study.sponsor }}</span>
              </div>
              <div v-if="study.status">
                <span class="metadata-label">Status:</span>
                <span class="metadata-value status-badge" :class="study.status.toLowerCase().replace('_', '-')">{{ formatStatus(study.status) }}</span>
              </div>
              <div v-if="study.source">
                <span class="metadata-label">Source:</span>
                <span class="metadata-value">{{ study.source }}</span>
              </div>
              <div v-if="study.nct_id">
                <span class="metadata-label">NCT ID:</span>
                <span class="metadata-value">
                  <a :href="`https://clinicaltrials.gov/ct2/show/${study.nct_id}`" target="_blank">{{ study.nct_id }}</a>
                </span>
              </div>
            </div>
          </div>
          <div class="detail-section">
            <strong>Description:</strong>
            <p>{{ study.description }}</p>
          </div>
          <div v-if="study.inclusion_criteria" class="detail-section">
            <strong>Inclusion Criteria:</strong>
            <p>{{ study.inclusion_criteria }}</p>
          </div>
          <div v-if="study.exclusion_criteria" class="detail-section">
            <strong>Exclusion Criteria:</strong>
            <p>{{ study.exclusion_criteria }}</p>
          </div>
        </div>
      </div>
      
      <!-- Loading more indicator -->
      <div v-if="isLoadingMore" class="loading-more">
        Loading more results...
      </div>
      
      <!-- End of results indicator -->
      <div v-if="!nextPageToken && clinicalTrialsResults.length > 0 && !isLoadingMore" class="end-of-results">
        No more results
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { API_ENDPOINTS } from '@/constants'

export default {
  name: 'ClinicalTrialsManagement',
  components: {
    Button,
    Input
  },
  data() {
    return {
      clinicalTrialsQuery: '',
      clinicalTrialsResults: [],
      clinicalTrialsError: '',
      isLoadingClinicalTrials: false,
      isImportingClinicalTrial: {},
      clinicalTrialsSearched: false,
      expandedStudies: {},
      importedNctIds: new Set(),
      nextPageToken: null,
      isLoadingMore: false
    }
  },
  mounted() {
    // Add scroll listener for infinite scroll
    window.addEventListener('scroll', this.handleScroll)
  },
  beforeUnmount() {
    // Remove scroll listener
    window.removeEventListener('scroll', this.handleScroll)
  },
  methods: {
    async searchClinicalTrials() {
      this.isLoadingClinicalTrials = true
      this.clinicalTrialsError = ''
      this.clinicalTrialsResults = []
      this.clinicalTrialsSearched = false
      this.nextPageToken = null
      try {
        const resp = await api.get('/api/admin/clinical-trials/search', { params: { query: this.clinicalTrialsQuery } })
        
        // Check if resp.data is the array directly or has a studies property
        const studies = Array.isArray(resp.data) ? resp.data : (resp.data.studies || [])
        
        this.clinicalTrialsResults = studies
        this.nextPageToken = resp.data.nextPageToken || null
        this.clinicalTrialsSearched = true
        
        // Check which studies are already imported
        await this.checkImportedStudies()
      } catch (err) {
        this.clinicalTrialsError = err.response?.data?.detail || 'Search failed'
      } finally {
        this.isLoadingClinicalTrials = false
      }
    },
    async loadMoreResults() {
      if (!this.nextPageToken || this.isLoadingMore || this.isLoadingClinicalTrials) {
        return
      }
      
      this.isLoadingMore = true
      try {
        const resp = await api.get('/api/admin/clinical-trials/search', { 
          params: { 
            query: this.clinicalTrialsQuery,
            page_token: this.nextPageToken
          } 
        })
        
        // Check if resp.data is the array directly or has a studies property
        const newStudies = Array.isArray(resp.data) ? resp.data : (resp.data.studies || [])
        
        this.clinicalTrialsResults = [...this.clinicalTrialsResults, ...newStudies]
        this.nextPageToken = resp.data.nextPageToken || null
        
        // Check which new studies are already imported
        await this.checkImportedStudies()
      } catch (err) {
        this.clinicalTrialsError = err.response?.data?.detail || 'Failed to load more results'
      } finally {
        this.isLoadingMore = false
      }
    },
    handleScroll() {
      // Check if user has scrolled to bottom
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop
      const scrollHeight = document.documentElement.scrollHeight
      const clientHeight = document.documentElement.clientHeight
      
      const distanceFromBottom = scrollHeight - (scrollTop + clientHeight)
      
      // Trigger load more when within 200px of bottom
      if (distanceFromBottom <= 200) {
        this.loadMoreResults()
      }
    },
    async importClinicalTrial(study) {
      this.isImportingClinicalTrial[study.nct_id] = true
      try {
        await api.post(`/api/admin/clinical-trials/import/${study.nct_id}`)
        alert('Study imported successfully!')
        
        // Add to imported set
        this.importedNctIds.add(study.nct_id)
        
        this.$emit('study-imported')
      } catch (err) {
        alert(err.response?.data?.detail || 'Import failed')
      } finally {
        this.isImportingClinicalTrial[study.nct_id] = false
      }
    },
    toggleStudy(nctId) {
      this.expandedStudies[nctId] = !this.expandedStudies[nctId]
    },
    async checkImportedStudies() {
      try {
        // Only check the NCT IDs currently on screen. This correctly flags
        // studies added one-by-one AND those loaded in bulk by the nightly
        // ctgov_sync_worker.py, without fetching the full (potentially huge)
        // studies table.
        const nctIds = this.clinicalTrialsResults
          .map(study => study.nct_id)
          .filter(Boolean)
        if (nctIds.length === 0) {
          return
        }
        const response = await api.post(
          API_ENDPOINTS.ADMIN_CLINICAL_TRIALS_IMPORTED_STATUS,
          { nct_ids: nctIds }
        )
        const imported = response.data?.imported || []
        // Union with any studies imported during this session so freshly
        // imported items stay flagged even before the next search/scroll.
        imported.forEach(nctId => this.importedNctIds.add(nctId))
      } catch (err) {
        console.error('Failed to check imported studies:', err)
      }
    },
    isStudyImported(nctId) {
      return this.importedNctIds.has(nctId)
    },
    formatStatus(status) {
      if (!status) return 'N/A'
      // Convert from UPPER_CASE to Title Case with spaces
      return status.split('_').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      ).join(' ')
    }
  }
}
</script>

<style scoped>
.admin-section {
  margin-bottom: 3rem;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.section-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
}

.message {
  margin-top: 1rem;
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  font-size: 0.9375rem;
}

.message.error {
  background-color: #fee2e2;
  color: #991b1b;
  border: 1px solid #ef4444;
}

.loading-state,
.empty-state {
  padding: 2rem;
  text-align: center;
  color: #64748b;
}

.loading-more {
  padding: 1.5rem;
  text-align: center;
  color: #64748b;
  font-size: 0.875rem;
  font-style: italic;
}

.end-of-results {
  padding: 1.5rem;
  text-align: center;
  color: #94a3b8;
  font-size: 0.875rem;
  border-top: 1px solid #e2e8f0;
  margin-top: 1rem;
}

.results-counter {
  position: sticky;
  top: 70px;
  padding: 0.75rem 1.5rem;
  text-align: center;
  background-color: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(8px);
  border-bottom: 1px solid rgba(226, 232, 240, 0.8);
  color: #475569;
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 1rem;
  z-index: 90;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.studies-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.study-item {
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  overflow: hidden;
  background-color: #ffffff;
}

.study-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
}

.study-info {
  flex: 1;
}

.study-info:hover {
  background-color: #f8fafc;
  cursor: pointer;
}

.study-actions {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.expand-icon {
  font-size: 0.75rem;
  color: #64748b;
  transition: transform 0.2s ease;
  display: inline-block;
  cursor: pointer;
  margin-left: 0.5rem;
}

.expand-icon.expanded {
  transform: rotate(180deg);
}

.study-name {
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 0.25rem;
}

.study-meta {
  display: flex;
  gap: 1rem;
  align-items: center;
  font-size: 0.875rem;
  color: #64748b;
}

.study-details {
  border-top: 1px solid #e2e8f0;
  padding: 1rem;
  background-color: #f8fafc;
}

.detail-section {
  margin-bottom: 1rem;
}

.detail-section:last-child {
  margin-bottom: 0;
}

.detail-section strong {
  display: block;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 0.5rem;
}

.detail-section p {
  color: #475569;
  line-height: 1.6;
  margin: 0;
  white-space: pre-wrap;
}

.study-metadata {
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 0.375rem;
  padding: 0.75rem;
}

.metadata-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 0.5rem;
}

.metadata-grid > div {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.metadata-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
}

.metadata-value {
  font-size: 0.875rem;
  color: #1e293b;
}

.metadata-value a {
  color: #3b82f6;
  text-decoration: none;
  font-weight: 500;
}

.metadata-value a:hover {
  text-decoration: underline;
}

.status-badge {
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-weight: 500;
  font-size: 0.75rem;
  display: inline-block;
}

.status-badge.recruiting {
  background-color: #d1fae5;
  color: #065f46;
}

.status-badge.not-yet-recruiting {
  background-color: #dbeafe;
  color: #1e40af;
}

/* ClinicalTrials.gov Section Styles */
.search-form {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.search-input {
  flex: 1;
  min-width: 200px;
}

.study-sponsor {
  color: #64748b;
  font-size: 0.875rem;
}

.study-source {
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-weight: 500;
  text-transform: uppercase;
  font-size: 0.75rem;
  background-color: #dbeafe;
  color: #1e40af;
}

.study-nctid {
  font-size: 0.875rem;
}

.study-nctid a {
  color: #3b82f6;
  text-decoration: none;
  font-weight: 500;
}

.study-nctid a:hover {
  text-decoration: underline;
}

@media (max-width: 768px) {
  .search-form {
    flex-direction: column;
    align-items: stretch;
  }

  .study-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }

  .metadata-grid {
    flex-direction: column;
  }
}

.refresh-btn {
  background-color: #3b82f6;
  color: #ffffff;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.refresh-btn:hover:not(:disabled) {
  background-color: #2563eb;
}

.refresh-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.imported-btn {
  background-color: #10b981 !important;
}

.imported-btn:hover {
  background-color: #10b981 !important;
}
</style>
